import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { confirmPasswordReset } from '../adminAuthApi';
import { auth as adminAuth } from '../adminAppAuth';
import { ADMIN_PATHS, adminPath } from '../../constants/adminPaths';
import '../styles/adminLoginPage.css';

function getQueryParam(name) {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(name);
  if (value) return value;

  const match = window.location.href.match(new RegExp(`[?&]${name}=([^&]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

const AdminResetPasswordPage = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const run = async () => {
      const oobCode = getQueryParam('oobCode') || getQueryParam('token');

      if (!oobCode) {
        setError('This password reset link is invalid or expired.');
        setLoading(false);
        return;
      }

      try {
        setActionCode(oobCode);
        setEmail(getQueryParam('email') || '');
      } catch {
        setError('This password reset link is invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await confirmPasswordReset(adminAuth, actionCode, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err?.message || 'Unable to reset password. Please request a new link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="adminLogin">
      <div className="adminLogin__bg" aria-hidden="true" />
      <main className="adminLogin__container">
        <section className="adminLogin__card" aria-label="Reset admin password">
          <header className="adminLogin__header">
            <div className="adminLogin__badge" aria-hidden="true">
              Admin
            </div>
            <h1 className="adminLogin__title">
              {loading ? 'Verifying link…' : success ? 'Password updated' : 'Reset password'}
            </h1>
            <p className="adminLogin__subtitle">
              {loading
                ? 'Please wait while we verify your reset link.'
                : success
                  ? 'Your password has been updated. You can now sign in with your new password.'
                  : email
                    ? `Choose a new password for ${email}.`
                    : 'Choose a new password for your admin account.'}
            </p>
          </header>

          {error ? (
            <div className="adminLogin__alert" role="alert">
              <span className="adminLogin__alertDot" aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}

          {passwordError ? (
            <div className="adminLogin__alert" role="alert">
              <span className="adminLogin__alertDot" aria-hidden="true" />
              <span>{passwordError}</span>
            </div>
          ) : null}

          {loading ? (
            <div className="adminLogin__loadingWrap" aria-busy="true">
              <span className="adminLogin__spinner" aria-hidden="true" />
            </div>
          ) : null}

          {!loading && !success && !error && actionCode ? (
            <form className="adminLogin__form" onSubmit={handleSubmit}>
              <label className="adminLogin__label">
                <span className="adminLogin__labelRow">
                  <span className="adminLogin__labelText">New password</span>
                  <button
                    type="button"
                    className="adminLogin__linkBtn"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </span>
                <input
                  className="adminLogin__input"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  disabled={submitting}
                  autoFocus
                />
              </label>

              <label className="adminLogin__label">
                <span className="adminLogin__labelText">Confirm password</span>
                <input
                  className="adminLogin__input"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  disabled={submitting}
                />
              </label>

              <button className="adminLogin__submit" type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="adminLogin__spinner" aria-hidden="true" />
                    Updating password…
                  </>
                ) : (
                  'Reset password'
                )}
              </button>
            </form>
          ) : null}

          {success ? (
            <div className="adminLogin__form">
              <RouterLink className="adminLogin__submit" to={adminPath(ADMIN_PATHS.LOGIN)} style={{ textDecoration: 'none' }}>
                Go to sign in
              </RouterLink>
            </div>
          ) : null}
        </section>
      </main>
    </div>
  );
};

export default AdminResetPasswordPage;
